import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useEffect, useRef, useState } from "react";
import { DailySellerMemberResponse } from "../../../api/member";
import { MemberGraphWrapper } from "../style";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  Filler
);

interface props {
  graphData: DailySellerMemberResponse[];
  filterType: string;
}

const MemberGraph = ({ graphData, filterType }: props) => {
  const [registerData, setRegisterData] = useState<number[]>([]);
  const [visitData, setVisitData] = useState<number[]>([]);
  const [animationDuration, setAnimationDuration] = useState<number>(500);
  const [labels, setLabels] = useState<string[]>([]);
  const [graphDate, setGraphDate] = useState<string[]>([]);
  const customImage = new Image();
  customImage.src = "/image-web/dashboard/chart/pointer.svg";
  const prevFilterType = useRef(filterType);

  useEffect(() => {
    if (filterType !== prevFilterType.current) {
      setAnimationDuration(500);
      prevFilterType.current = filterType;
      setTimeout(() => {
        setAnimationDuration(0);
      }, 500);
    } else {
      setAnimationDuration(0);
    }
  }, [filterType]);

  useEffect(() => {
    setLabels(() => {
      if (filterType === "daily") {
        let newLabels = graphData.map((item) => getWeek(item.day));
        return newLabels;
      } else {
        let newLabels = graphData.map((item) => item.day);
        return newLabels;
      }
    });
    setGraphDate(() => {
      if (filterType === "daily") {
        let newLabels = graphData.map((item) => getDayWeek(item.day));
        return newLabels;
      } else {
        let newLabels = graphData.map((item) => item.day);
        return newLabels;
      }
    });
    setRegisterData(() => {
      let newData = graphData.map((item) => item.registerCount);
      return newData;
    });
    setVisitData(() => {
      let newData = graphData.map((item) => item.visitCount);
      return newData;
    });
  }, [graphData]);

  const getWeek = (value: string) => {
    const [year, month, day] = value.split("-");
    const date = new Date(+year, +month - 1, +day);

    const dayOfWeek = date.getDay();

    const days = ["일", "월", "화", "수", "목", "금", "토"];

    return days[dayOfWeek];
  };

  const getDayWeek = (value: string) => {
    const [year, month, day] = value.split("-");
    const date = new Date(+year, +month - 1, +day);

    const dayOfWeek = date.getDay();

    const days = ["일", "월", "화", "수", "목", "금", "토"];
    const week = days[dayOfWeek];

    return `${month}/${day} ${week}`;
  };

  const data = {
    labels: labels,
    datasets: [
      {
        data: visitData,
        borderColor: "#7459D9",
        backgroundColor: (ctx: any) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;

          if (!chartArea) return null;

          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );

          gradient.addColorStop(0, "rgba(116, 89, 217, 0.15)");
          gradient.addColorStop(1, "rgba(116, 89, 217, 0)");

          return gradient;
        },
        tension: 0.4,
        pointRadius: 0,
        pointStyle: customImage,
        hitRadius: 40,
        fill: true,
        datalabels: {
          display: false,
        },
      },
      {
        data: registerData,
        borderColor: "#7459D9",
        backgroundColor: (ctx: any) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;

          if (!chartArea) return null;

          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );

          gradient.addColorStop(0, "rgba(116, 89, 217, 0.15)");
          gradient.addColorStop(1, "rgba(116, 89, 217, 0)");

          return gradient;
        },
        tension: 0.4,
        pointRadius: 0,
        pointStyle: customImage,
        hitRadius: 40,
        fill: true,
        datalabels: {
          display: false,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: function (tooltip: any) {
          const tooltipEl = document.getElementById("chartjs-tooltip");
          const line = document.getElementById("line");
          if (!tooltipEl || !line) return;

          if (tooltip.tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0";
            line.style.opacity = "0";
            line.style.height = "0";
            return;
          }

          const chart = tooltip.chart;
          const tooltipDataPoint = tooltip.tooltip.dataPoints[0];
          const x = tooltipDataPoint.element.x;
          const y = tooltipDataPoint.element.y;
          const lineX = x + 38;
          const lineY = y + 348;
          const tooltipX = x - 68;
          const tooltipY = y + 200;
          const lineHeight = chart.scales["x"].top - y - 10;
          const height = y + 348 > 818 ? 0 : lineHeight;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.left = tooltipX + "px";
          tooltipEl.style.top = tooltipY + "px";

          line.style.opacity = "1";
          line.style.height = height + "px";
          line.style.left = lineX + "px";
          line.style.top = lineY + "px";

          const visitList = chart.config._config.data.datasets[0].data;
          const registerList = chart.config._config.data.datasets[1].data;
          const labelList = chart.config._config.data.labels;
          const dataDate = tooltipDataPoint.label;

          const index = labelList.findIndex(
            (label: string) => label === dataDate
          );

          tooltipEl.innerHTML = `
            <div class="wrapper">
              <div class="header">${graphDate[index]}</div>
              <div class="content">
                <div class="info">
                  <div>방문 회원</div>
                  <div class="value">${visitList[index]} 명</div>
                </div>
                <div class="info">
                  <div>가입한 신규 회원</div>
                  <div class="value">${registerList[index]} 명</div>
                </div>
              </div>
            </div>
          `;
        },
      },
    },
    scales: {
      y: {
        afterDataLimits: (scale: any) => {
          scale.max = scale.max * 1.2;
        },
        beginAtZero: true,
        ticks: {
          stepSize: 25,
          color: "#ECE9F1",
          font: {
            size: 17,
            color: "#A2A3A5",
            weight: 400,
            family: "Pretendard",
          },
          padding: 20,
        },
        border: {
          width: 0,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#ECE9F1",
          padding: 10,
          font: {
            size: filterType === "weekly" ? 13 : 17,
            color: "#A2A3A5",
            weight: 400,
            family: "Pretendard",
          },
        },
        offset: true,
      },
    },
    animation: {
      duration: animationDuration,
    },
  };

  return (
    <>
      <MemberGraphWrapper>
        <Chart data={data} options={options} type="line" />
        <div id="chartjs-tooltip" />
        <div id="line" />
      </MemberGraphWrapper>
    </>
  );
};

export default MemberGraph;
